import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Building, User, Phone, Wrench, CheckCircle, ArrowLeft } from 'lucide-react';
import { publicAPI } from './api';
import CancelReservation from './CancelReservation';

const HeatingAppointmentApp = () => {
  // Always call hooks first, before any conditional logic
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    association: '',
    firstName: '',
    lastName: '',
    apartment: '',
    phone: '',
    email: '',
    serviceId: '',
    month: '',
    day: '',
    timeSlot: ''
  });

  // Real data from server
  const [associations, setAssociations] = useState([]);
  const [services, setServices] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Function to fetch associations
  const fetchAssociations = async () => {
    try {
      setLoading(true);
      const associationsData = await publicAPI.getAssociations();
      setAssociations(associationsData);
    } catch (error) {
      console.error('Error fetching associations:', error);
      setError('Eroare la încărcarea asociațiilor. Vă rugăm să încercați din nou.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available services for selected association
  const fetchAvailableServices = async (associationId) => {
    if (!associationId) {
      setServices([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const servicesData = await publicAPI.getAssociationServices(associationId);
      
      // Filter services to only show active ones with available slots
      const availableServices = servicesData.filter(service => 
        service.active && service.slots_generated
      );
      
      // Further filter services that have future slots available
      const servicesWithFutureSlots = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      
      for (const service of availableServices) {
        try {
          // Get available slots for this service
          const slotsData = await publicAPI.getAvailableSlots(service.id);
          
          // Check if any slots are in the future
          const hasFutureSlots = slotsData.some(dateGroup => {
            return dateGroup.slots && dateGroup.slots.some(slot => {
              const slotDate = new Date(slot.slot_date);
              return slotDate >= today;
            });
          });
          
          if (hasFutureSlots) {
            servicesWithFutureSlots.push(service);
          }
        } catch (error) {
          console.error(`Error checking slots for service ${service.id}:`, error);
          // If we can't check slots, include the service to be safe
          servicesWithFutureSlots.push(service);
        }
      }
      
      setServices(servicesWithFutureSlots);
      
      // Auto-select if only one service is available
      if (servicesWithFutureSlots.length === 1) {
        setFormData(prev => ({
          ...prev,
          serviceId: servicesWithFutureSlots[0].id
        }));
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setError('Eroare la încărcarea serviciilor. Vă rugăm să încercați din nou.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // All useEffect hooks must be called before any conditional logic
  // Load associations on component mount
  useEffect(() => {
    fetchAssociations();
  }, []);

  // Clear any existing reservation data on page load/refresh
  useEffect(() => {
    // Clear localStorage to start fresh
    localStorage.removeItem('currentReservationId');
    
    // Reset form to initial state
    setFormData({
      association: '',
      firstName: '',
      lastName: '',
      apartment: '',
      phone: '',
      email: '',
      serviceId: '',
      month: '',
      day: '',
      timeSlot: ''
    });
    
    // Ensure we start at step 1
    setCurrentStep(1);
  }, []);

  // Fetch services when association is selected
  useEffect(() => {
    if (formData.association) {
      fetchAvailableServices(formData.association);
    }
  }, [formData.association]);
  
  // Handle month data when available slots change
  useEffect(() => {
    if (currentStep === 3 && availableSlots && availableSlots.length > 0) {
      console.log('Processing available slots for month selection');
      console.log('Available slots data:', availableSlots);
      
      // Filter out past months
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      const futureMonths = availableSlots
        .map(slotGroup => {
          // Convert English month name to Romanian
          const romanianMonth = convertMonthToRomanian(slotGroup.month);
          return {
            englishMonth: slotGroup.month,
            romanianMonth: romanianMonth,
            slotDate: slotGroup.slot_date
          };
        })
        .filter(monthData => {
          const monthIndex = months.indexOf(monthData.romanianMonth);
          if (monthIndex === -1) return false;
          
          // Check if month is in current year and not past
          const slotDate = new Date(monthData.slotDate);
          const slotYear = slotDate.getFullYear();
          const slotMonth = slotDate.getMonth();
          
          if (slotYear > currentYear) return true;
          if (slotYear < currentYear) return false;
          
          return slotMonth >= currentMonth;
        })
        .map(monthData => monthData.romanianMonth)
        .filter((month, index, array) => array.indexOf(month) === index) // Remove duplicates
        .sort((a, b) => months.indexOf(a) - months.indexOf(b));
      
      console.log('Setting future months:', futureMonths);
      setAvailableMonths(futureMonths);
    }
  }, [currentStep, availableSlots]);

  // Handle day data when month is selected
  useEffect(() => {
    if (currentStep === 4 && formData.month && availableSlots && availableSlots.length > 0) {
      console.log('Processing available slots for day selection');
      console.log('Selected month:', formData.month);
      console.log('Available slots:', availableSlots);
      
      // Filter out past days
      const today = new Date();
      const currentDay = today.getDate();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      // Convert Romanian month back to English for comparison
      const monthMap = {
        'Ianuarie': 'January',
        'Februarie': 'February', 
        'Martie': 'March',
        'Aprilie': 'April',
        'Mai': 'May',
        'Iunie': 'June',
        'Iulie': 'July',
        'August': 'August',
        'Septembrie': 'September',
        'Octombrie': 'October',
        'Noiembrie': 'November',
        'Decembrie': 'December'
      };
      const englishMonth = monthMap[formData.month];
      
      const futureDays = availableSlots
        .filter(slotGroup => {
          // Convert English month to Romanian and compare
          const romanianMonth = convertMonthToRomanian(slotGroup.month);
          return romanianMonth === formData.month;
        })
        .map(slotGroup => {
          const slotDate = new Date(slotGroup.slot_date);
          return slotDate.getDate();
        })
        .filter(day => {
          const monthIndex = months.indexOf(formData.month);
          if (monthIndex === -1) return false;
          
          // Check if day is in current month and not past
          if (monthIndex > currentMonth) return true;
          if (monthIndex < currentMonth) return false;
          
          return day >= currentDay;
        })
        .filter((day, index, array) => array.indexOf(day) === index) // Remove duplicates
        .sort((a, b) => a - b);
      
      console.log('Setting future days:', futureDays);
      setAvailableDays(futureDays);
    }
  }, [currentStep, formData.month, availableSlots]);

  // Handle time slot data when day is selected
  useEffect(() => {
    if (currentStep === 5 && formData.day && availableSlots && availableSlots.length > 0) {
      console.log('Processing available slots for time selection');
      console.log('Selected day:', formData.day);
      console.log('Selected month:', formData.month);
      
      // Filter out past time slots
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      // Convert Romanian month back to English for comparison
      const monthMap = {
        'Ianuarie': 'January',
        'Februarie': 'February', 
        'Martie': 'March',
        'Aprilie': 'April',
        'Mai': 'May',
        'Iunie': 'June',
        'Iulie': 'July',
        'August': 'August',
        'Septembrie': 'September',
        'Octombrie': 'October',
        'Noiembrie': 'November',
        'Decembrie': 'December'
      };
      const englishMonth = monthMap[formData.month];
      
      const futureTimeSlots = availableSlots
        .filter(slotGroup => {
          // Convert English month to Romanian and compare
          const romanianMonth = convertMonthToRomanian(slotGroup.month);
          const slotDate = new Date(slotGroup.slot_date);
          const slotDay = slotDate.getDate();
          
          return romanianMonth === formData.month && slotDay === parseInt(formData.day);
        })
        .flatMap(slotGroup => 
          slotGroup.slots.map(slot => ({
            id: slot.id,
            start_time: slot.start_time,
            end_time: slot.end_time,
            display: `${slot.start_time} - ${slot.end_time}`
          }))
        )
        .filter(slot => {
          // Check if time slot is not in the past
          const [startHour, startMinute] = slot.start_time.split(':').map(Number);
          const slotTime = startHour * 60 + startMinute;
          const currentTime = currentHour * 60 + currentMinute;
          
          // Get the selected date
          const selectedDate = new Date(`${englishMonth} ${formData.day}, ${new Date().getFullYear()}`);
          const today = new Date();
          const isToday = selectedDate.toDateString() === today.toDateString();
          
          // Only filter by time if it's today's date
          if (isToday) {
            return slotTime > currentTime;
          } else {
            // For future dates, show all time slots
            return true;
          }
        })
        .sort((a, b) => a.start_time.localeCompare(b.start_time));
      
      console.log('Setting future time slots:', futureTimeSlots);
      setAvailableTimeSlots(futureTimeSlots);
      
      // Store available time slots in form data for later use
      setFormData(prev => ({
        ...prev,
        availableTimeSlots: futureTimeSlots
      }));
    }
  }, [currentStep, formData.day, formData.month, availableSlots]);

  // Check if this is a cancellation route after all hooks
  const path = window.location.pathname;
  const cancelMatch = path.match(/^\/cancel\/(.+)$/);
  
  if (cancelMatch) {
    const reservationId = cancelMatch[1];
    console.log('Cancel route detected. Path:', path);
    console.log('Extracted reservation ID:', reservationId);
    console.log('Reservation ID type:', typeof reservationId);
    console.log('Reservation ID length:', reservationId?.length);
    return <CancelReservation reservationId={reservationId} />;
  }

  const months = [
    'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
    'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
  ];


  // Function to convert English month names to Romanian
  const convertMonthToRomanian = (englishMonth) => {
    const monthMap = {
      'January': 'Ianuarie',
      'February': 'Februarie', 
      'March': 'Martie',
      'April': 'Aprilie',
      'May': 'Mai',
      'June': 'Iunie',
      'July': 'Iulie',
      'August': 'August',
      'September': 'Septembrie',
      'October': 'Octombrie',
      'November': 'Noiembrie',
      'December': 'Decembrie'
    };
    return monthMap[englishMonth] || englishMonth;
  };



  // Validation functions
  const validateField = (field, value) => {
    const errors = { ...validationErrors };
    
    switch (field) {
      case 'firstName':
        if (!value || value.trim().length < 2) {
          errors.firstName = 'Prenumele trebuie să aibă cel puțin 2 caractere';
        } else {
          delete errors.firstName;
        }
        break;
        
      case 'lastName':
        if (!value || value.trim().length < 2) {
          errors.lastName = 'Numele trebuie să aibă cel puțin 2 caractere';
        } else {
          delete errors.lastName;
        }
        break;
        
      case 'apartment':
        if (!value || value.trim().length < 1) {
          errors.apartment = 'Numărul apartamentului este obligatoriu';
        } else {
          delete errors.apartment;
        }
        break;
        
      case 'phone':
        if (!value || value.trim().length < 5) {
          errors.phone = 'Numărul de telefon trebuie să aibă cel puțin 5 cifre';
        } else if (!/^[\+]?[0-9\s\-\(\)]+$/.test(value)) {
          errors.phone = 'Numărul de telefon conține caractere invalide';
        } else {
          delete errors.phone;
        }
        break;
        
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = 'Adresa de email nu este validă';
        } else {
          delete errors.email;
        }
        break;
        
      case 'serviceId':
        if (!value) {
          errors.serviceId = 'Selectează un serviciu';
        } else {
          delete errors.serviceId;
        }
        break;
        
      case 'month':
        if (!value) {
          errors.month = 'Selectează o lună';
        } else {
          delete errors.month;
        }
        break;
        
      case 'day':
        if (!value) {
          errors.day = 'Selectează o zi';
        } else {
          delete errors.day;
        }
        break;
        
      case 'timeSlot':
        if (!value) {
          errors.timeSlot = 'Selectează un interval orar';
        } else {
          delete errors.timeSlot;
        }
        break;
        
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForm = () => {
    const fields = ['firstName', 'lastName', 'apartment', 'phone', 'serviceId'];
    let isValid = true;
    
    fields.forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });
    
    // Validate email if provided
    if (formData.email) {
      validateField('email', formData.email);
    }
    
    // Validate scheduling fields if we're on those steps
    if (currentStep >= 3) {
      if (formData.month && !validateField('month', formData.month)) {
        isValid = false;
      }
    }
    if (currentStep >= 4) {
      if (formData.day && !validateField('day', formData.day)) {
        isValid = false;
      }
    }
    if (currentStep >= 5) {
      if (formData.timeSlot && !validateField('timeSlot', formData.timeSlot)) {
        isValid = false;
      }
    }
    
    return isValid;
  };



  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Validate field on change
    validateField(field, value);
  };

  const handleNext = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate form before proceeding
      if (currentStep === 2 && !validateForm()) {
        setLoading(false);
        return;
      }
      
      console.log('Current step:', currentStep);
      console.log('Form data:', formData);
      
      if (currentStep === 1 && formData.association) {
        // Step 1: Create reservation
        const selectedAssociation = associations.find(assoc => assoc.id === formData.association);
        
        if (!selectedAssociation) {
          setError('Asociația selectată nu a fost găsită.');
          return;
        }
        
        const reservationData = {
          association_id: selectedAssociation.id
        };
        
        const newReservation = await publicAPI.createReservation(reservationData);
        
        setFormData(prev => ({
          ...prev,
          reservationId: newReservation.id
        }));
        
        setCurrentStep(prev => prev + 1);
        
      } else if (currentStep === 2) {
        // Step 2: Update person data (only if not already completed)
        console.log('=== STEP 2: Person Data + Service Selection ===');
        console.log('Form data serviceId:', formData.serviceId);
        const reservation = await publicAPI.getReservation(formData.reservationId);
        console.log('Current reservation status:', reservation.status);
        
        if (reservation.status === 'pending') {
          // Person data not yet submitted, update it
          console.log('Updating person data...');
          const personData = {
            first_name: formData.firstName,
            last_name: formData.lastName,
            apartment_number: formData.apartment,
            phone: formData.phone,
            email: formData.email
          };
          
          await publicAPI.updatePersonData(formData.reservationId, personData);
          console.log('Person data updated successfully');
          
          // After updating person data, select the service
          console.log('Selecting service after person data update...');
          try {
            const result = await publicAPI.selectService(formData.reservationId, formData.serviceId);
            console.log('Service selection successful:', result);
          } catch (error) {
            console.error('Service selection failed:', error);
            setError('Eroare la selectarea serviciului. Vă rugăm să încercați din nou.');
            return;
          }
        } else if (reservation.status === 'person_completed') {
          // Person data already submitted, but service not yet selected
          console.log('Person data already completed, selecting service...');
          try {
            const result = await publicAPI.selectService(formData.reservationId, formData.serviceId);
            console.log('Service selection successful:', result);
          } catch (error) {
            console.error('Service selection failed:', error);
            setError('Eroare la selectarea serviciului. Vă rugăm să încercați din nou.');
            return;
          }
        } else {
          // Service already selected, just continue
          console.log('Service already selected, continuing to next step');
        }
        
        // Services are already loaded from the initial call, no need to fetch again
        console.log('Services already loaded, skipping getAvailableServices call');
        
        // Get available slots for the selected service
        console.log('Getting slots for service:', formData.serviceId);
        const slotsData = await publicAPI.getAvailableSlots(formData.serviceId);
        console.log('Available slots data:', slotsData);
        setAvailableSlots(slotsData);
        
        setCurrentStep(prev => prev + 1);
        
      } else if (currentStep === 3) {
        // Step 3: Month selection - just move to next step
        // The useEffect will handle processing the available months
        console.log('Step 3: Moving to day selection');
        setCurrentStep(prev => prev + 1);
        
      } else if (currentStep === 4) {
        // Step 4: Day selection - just move to next step
        // The useEffect will handle processing the available days
        console.log('Step 4: Moving to time selection');
        setCurrentStep(prev => prev + 1);
        
      } else if (currentStep === 5) {
        // Step 5: Time slot selection - just move to next step
        // The useEffect will handle processing the available time slots
        console.log('Step 5: Moving to confirmation');
        setCurrentStep(prev => prev + 1);
        
      } else {
        // For other steps, just move to next step
        setCurrentStep(prev => prev + 1);
      }
      
    } catch (error) {
      console.error('Error in handleNext:', error);
      setError('Eroare la procesarea cererii. Vă rugăm să încercați din nou.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    // Clear validation errors when going back
    setValidationErrors({});
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Debug: Log the entire formData to see what's missing
      console.log('Full formData in handleConfirm:', formData);
      
      // Find the selected time slot
      const selectedSlot = formData.availableTimeSlots?.find(slot => slot.display === formData.timeSlot);
      
      if (!selectedSlot) {
        setError('Intervalul orar selectat nu a fost găsit.');
        return;
      }
      
      if (!formData.serviceId) {
        setError('Serviciul nu a fost selectat. Vă rugăm să selectați un serviciu.');
        return;
      }
      
      console.log('Available time slots:', formData.availableTimeSlots);
      console.log('Selected time slot:', formData.timeSlot);
      console.log('Found slot:', selectedSlot);
      
      console.log('Confirming reservation with:', {
        reservationId: formData.reservationId,
        serviceId: formData.serviceId,
        slotId: selectedSlot.id
      });
      
      // Check reservation status before attempting to reserve
      console.log('Checking reservation status before reserving...');
      const currentReservation = await publicAPI.getReservation(formData.reservationId);
      console.log('Current reservation status:', currentReservation.status);
      
      if (currentReservation.status !== 'month_selected' && currentReservation.status !== 'time_selected') {
        setError(`Rezervarea nu este în statusul corect. Status actual: ${currentReservation.status}. Vă rugăm să reîncercați.`);
        return;
      }
      
      // Reserve the specific slot (this will handle status transitions automatically)
      await publicAPI.reserveSlot(formData.reservationId, selectedSlot.id);
      
      // Send confirmation email
      try {
        await publicAPI.sendConfirmationEmail(formData.reservationId);
        alert('🎉 Programarea a fost confirmată cu succes! Un email de confirmare a fost trimis la adresa ta.');
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        alert('✅ Programarea a fost confirmată cu succes! (Email-ul de confirmare nu a putut fi trimis - va trebui configurat SMTP)');
      }
      
      // Reset form
      setFormData({
        association: '',
        firstName: '',
        lastName: '',
        apartment: '',
        phone: '',
        email: '',
        serviceId: '',
        month: '',
        day: '',
        timeSlot: ''
      });
      setValidationErrors({});
      setCurrentStep(1);
      
    } catch (error) {
      console.error('Error confirming reservation:', error);
      setError('Eroare la confirmarea programării. Vă rugăm să încercați din nou.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Building className="mx-auto h-12 w-12 mb-4" style={{color: '#971b1e'}} />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Selectează Asociația</h2>
        <p className="text-gray-600">Alege asociația de proprietari din care faci parte</p>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={fetchAssociations}
              className="text-[#971b1e] hover:text-[#7a1619] font-medium"
            >
              Încearcă din nou
            </button>
          </div>
        </div>
      )}
      
      {/* Loading State */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#971b1e] mx-auto mb-4"></div>
          <p className="text-gray-500">Se încarcă asociațiile...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {associations.map((association) => (
            <button
              key={association.id}
              onClick={() => handleInputChange('association', association.id)}
              className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                formData.association === association.id
                  ? 'border-[#971b1e] bg-red-50 text-[#971b1e]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Building className="h-5 w-5 mr-3" />
                {association.name}
              </div>
            </button>
          ))}
        </div>
      )}
      
      <button
        onClick={handleNext}
        disabled={!formData.association || loading}
        className="w-full text-white py-3 px-4 rounded-lg font-semibold disabled:cursor-not-allowed transition-colors"
        style={{
          backgroundColor: formData.association && !loading ? '#971b1e' : '#d1d5db',
        }}
        onMouseEnter={(e) => {
          if (formData.association && !loading) {
            e.target.style.backgroundColor = '#7a1619';
          }
        }}
        onMouseLeave={(e) => {
          if (formData.association && !loading) {
            e.target.style.backgroundColor = '#971b1e';
          }
        }}
      >
        {loading ? 'Se încarcă...' : 'Continuă'}
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <User className="mx-auto h-12 w-12 mb-4" style={{color: '#971b1e'}} />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Datele Tale</h2>
        <p className="text-gray-600">Completează informațiile personale</p>
      </div>
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nume</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 ${
                validationErrors.lastName 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:border-[#971b1e]'
              }`}
              style={{'--tw-ring-color': validationErrors.lastName ? '#ef4444' : '#971b1e'}}
              placeholder="Introdu numele"
            />
            {validationErrors.lastName && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.lastName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prenume</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 ${
                validationErrors.firstName 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:border-[#971b1e]'
              }`}
              style={{'--tw-ring-color': validationErrors.firstName ? '#ef4444' : '#971b1e'}}
              placeholder="Introdu prenumele"
            />
            {validationErrors.firstName && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.firstName}</p>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Numărul apartamentului</label>
          <input
            type="text"
            value={formData.apartment}
            onChange={(e) => handleInputChange('apartment', e.target.value)}
            className={`w-full p-3 border rounded-lg focus:ring-2 ${
              validationErrors.apartment 
                ? 'border-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:border-[#971b1e]'
            }`}
            style={{'--tw-ring-color': validationErrors.apartment ? '#ef4444' : '#971b1e'}}
            placeholder="ex: 12A"
          />
          {validationErrors.apartment && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.apartment}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Număr de telefon</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className={`w-full p-3 border rounded-lg focus:ring-2 ${
              validationErrors.phone 
                ? 'border-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:border-[#971b1e]'
            }`}
            style={{'--tw-ring-color': validationErrors.phone ? '#ef4444' : '#971b1e'}}
            placeholder="ex: +40722123456"
          />
          {validationErrors.phone && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresa de email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full p-3 border rounded-lg focus:ring-2 ${
              validationErrors.email 
                ? 'border-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:border-[#971b1e]'
            }`}
            style={{'--tw-ring-color': validationErrors.email ? '#ef4444' : '#971b1e'}}
            placeholder="ex: nume@email.com"
          />
          {validationErrors.email && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {services.length === 1 ? 'Serviciul disponibil (selectat automat)' : 'Serviciul disponibil'}
          </label>
          {loading ? (
            <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
              Se încarcă serviciile...
            </div>
          ) : services.length === 0 ? (
            <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
              Nu există servicii disponibile pentru această asociație
            </div>
          ) : services.length === 1 ? (
            <div className="w-full p-3 border border-gray-300 rounded-lg bg-green-50 text-green-700 border-green-200">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                {services[0].name}
              </div>
              <p className="text-sm text-green-600 mt-1">Selectat automat</p>
            </div>
          ) : (
            <div>
              <select
                value={formData.serviceId}
                onChange={(e) => handleInputChange('serviceId', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 ${
                  validationErrors.serviceId 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:border-[#971b1e]'
                }`}
                style={{'--tw-ring-color': validationErrors.serviceId ? '#ef4444' : '#971b1e'}}
              >
                <option value="">Selectează un serviciu</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
              {validationErrors.serviceId && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.serviceId}</p>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex space-x-4">
        <button
          onClick={handleBack}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
          style={{backgroundColor: '#3d3c41'}}
          onMouseEnter={(e) => {e.target.style.backgroundColor = '#2d2c31'}}
          onMouseLeave={(e) => {e.target.style.backgroundColor = '#3d3c41'}}
        >
          <ArrowLeft className="h-5 w-5 inline mr-2" />
          Înapoi
        </button>
        <button
          onClick={handleNext}
          disabled={!formData.firstName || !formData.lastName || !formData.apartment || !formData.phone || !formData.email || !formData.serviceId}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold disabled:cursor-not-allowed transition-colors"
          style={{
            backgroundColor: (formData.firstName && formData.lastName && formData.apartment && formData.phone && formData.email && formData.serviceId) ? '#971b1e' : '#d1d5db',
          }}
          onMouseEnter={(e) => {
            if (formData.firstName && formData.lastName && formData.apartment && formData.phone && formData.email && formData.serviceId) {
              e.target.style.backgroundColor = '#7a1619';
            }
          }}
          onMouseLeave={(e) => {
            if (formData.firstName && formData.lastName && formData.apartment && formData.phone && formData.email && formData.serviceId) {
              e.target.style.backgroundColor = '#971b1e';
            }
          }}
        >
          Continuă
        </button>
      </div>
    </div>
  );


  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Calendar className="mx-auto h-12 w-12 mb-4" style={{color: '#971b1e'}} />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Selectează Luna</h2>
        <p className="text-gray-600">Alege luna pentru programare</p>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#971b1e] mx-auto mb-4"></div>
          <p className="text-gray-500">Se încarcă lunile disponibile...</p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-2 gap-3">
            {(availableMonths || []).map((month, index) => (
              <button
                key={index}
                onClick={() => handleInputChange('month', month)}
                className={`p-4 text-center rounded-lg border-2 transition-all ${
                  formData.month === month
                    ? 'border-[#971b1e] bg-red-50 text-[#971b1e]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {month}
              </button>
            ))}
          </div>
          {validationErrors.month && (
            <p className="text-red-500 text-sm mt-2">{validationErrors.month}</p>
          )}
        </div>
      )}
      
      <div className="flex space-x-4">
        <button
          onClick={handleBack}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
          style={{backgroundColor: '#3d3c41'}}
          onMouseEnter={(e) => {e.target.style.backgroundColor = '#2d2c31'}}
          onMouseLeave={(e) => {e.target.style.backgroundColor = '#3d3c41'}}
        >
          <ArrowLeft className="h-5 w-5 inline mr-2" />
          Înapoi
        </button>
        <button
          onClick={handleNext}
          disabled={!formData.month}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold disabled:cursor-not-allowed transition-colors"
          style={{
            backgroundColor: formData.month ? '#971b1e' : '#d1d5db',
          }}
          onMouseEnter={(e) => {
            if (formData.month) {
              e.target.style.backgroundColor = '#7a1619';
            }
          }}
          onMouseLeave={(e) => {
            if (formData.month) {
              e.target.style.backgroundColor = '#971b1e';
            }
          }}
        >
          Continuă
        </button>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Calendar className="mx-auto h-12 w-12 mb-4" style={{color: '#971b1e'}} />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Selectează Ziua</h2>
        <p className="text-gray-600">Zilele disponibile în {formData.month}</p>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#971b1e] mx-auto mb-4"></div>
          <p className="text-gray-500">Se încarcă zilele disponibile...</p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-4 gap-3">
            {(availableDays || []).map((day, index) => (
              <button
                key={index}
                onClick={() => handleInputChange('day', day)}
                className={`p-4 text-center rounded-lg border-2 transition-all ${
                  formData.day === day
                    ? 'border-[#971b1e] bg-red-50 text-[#971b1e]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {day}
              </button>
            ))}
          </div>
          {validationErrors.day && (
            <p className="text-red-500 text-sm mt-2">{validationErrors.day}</p>
          )}
        </div>
      )}
      
      {(!availableDays || availableDays.length === 0) && !loading && (
        <div className="text-center py-8">
          <p className="text-gray-500">Nu sunt zile disponibile pentru {formData.month}</p>
        </div>
      )}
      
      <div className="flex space-x-4">
        <button
          onClick={handleBack}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
          style={{backgroundColor: '#3d3c41'}}
          onMouseEnter={(e) => {e.target.style.backgroundColor = '#2d2c31'}}
          onMouseLeave={(e) => {e.target.style.backgroundColor = '#3d3c41'}}
        >
          <ArrowLeft className="h-5 w-5 inline mr-2" />
          Înapoi
        </button>
        <button
          onClick={handleNext}
          disabled={!formData.day}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold disabled:cursor-not-allowed transition-colors"
          style={{
            backgroundColor: formData.day ? '#971b1e' : '#d1d5db',
          }}
          onMouseEnter={(e) => {
            if (formData.day) {
              e.target.style.backgroundColor = '#7a1619';
            }
          }}
          onMouseLeave={(e) => {
            if (formData.day) {
              e.target.style.backgroundColor = '#971b1e';
            }
          }}
        >
          Continuă
        </button>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Clock className="mx-auto h-12 w-12 mb-4" style={{color: '#971b1e'}} />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Selectează Ora</h2>
        <p className="text-gray-600">Intervalele disponibile pentru {formData.day} {formData.month}</p>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#971b1e] mx-auto mb-4"></div>
          <p className="text-gray-500">Se încarcă intervalele orare...</p>
        </div>
      ) : (
        <div>
          <div className="space-y-3">
            {(availableTimeSlots || []).map((slot, index) => (
              <button
                key={slot.id}
                onClick={() => handleInputChange('timeSlot', slot.display)}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                  formData.timeSlot === slot.display
                    ? 'border-[#971b1e] bg-red-50 text-[#971b1e]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-3" />
                  {slot.display}
                </div>
              </button>
            ))}
          </div>
          {validationErrors.timeSlot && (
            <p className="text-red-500 text-sm mt-2">{validationErrors.timeSlot}</p>
          )}
        </div>
      )}
      
      {(!availableTimeSlots || availableTimeSlots.length === 0) && !loading && (
        <div className="text-center py-8">
          <p className="text-gray-500">Nu sunt intervale orare disponibile</p>
        </div>
      )}
      
      <div className="flex space-x-4">
        <button
          onClick={handleBack}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
          style={{backgroundColor: '#3d3c41'}}
          onMouseEnter={(e) => {e.target.style.backgroundColor = '#2d2c31'}}
          onMouseLeave={(e) => {e.target.style.backgroundColor = '#3d3c41'}}
        >
          <ArrowLeft className="h-5 w-5 inline mr-2" />
          Înapoi
        </button>
        <button
          onClick={handleNext}
          disabled={!formData.timeSlot}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold disabled:cursor-not-allowed transition-colors"
          style={{
            backgroundColor: formData.timeSlot ? '#971b1e' : '#d1d5db',
          }}
          onMouseEnter={(e) => {
            if (formData.timeSlot) {
              e.target.style.backgroundColor = '#7a1619';
            }
          }}
          onMouseLeave={(e) => {
            if (formData.timeSlot) {
              e.target.style.backgroundColor = '#971b1e';
            }
          }}
        >
          Confirmă
        </button>
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-6 relative">
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Se confirmă programarea...</p>
          </div>
        </div>
      )}
      <div className="text-center">
        <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Confirmă Programarea</h2>
        <p className="text-gray-600">Verifică detaliile programării</p>
      </div>
      
      <div className="bg-gray-50 p-6 rounded-lg space-y-4">
        <div className="flex items-center">
          <Building className="h-5 w-5 text-gray-500 mr-3" />
          <div>
            <p className="font-medium text-gray-700">Asociația:</p>
            <p className="text-gray-600">
              {associations.find(a => a.id === formData.association)?.name || 'Asociație selectată'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center">
          <User className="h-5 w-5 text-gray-500 mr-3" />
          <div>
            <p className="font-medium text-gray-700">Client:</p>
            <p className="text-gray-600">{formData.firstName} {formData.lastName}</p>
          </div>
        </div>
        
        <div className="flex items-center">
          <Building className="h-5 w-5 text-gray-500 mr-3" />
          <div>
            <p className="font-medium text-gray-700">Apartament:</p>
            <p className="text-gray-600">{formData.apartment}</p>
          </div>
        </div>
        
        <div className="flex items-center">
          <Phone className="h-5 w-5 text-gray-500 mr-3" />
          <div>
            <p className="font-medium text-gray-700">Telefon:</p>
            <p className="text-gray-600">{formData.phone}</p>
          </div>
        </div>
        
        <div className="flex items-center">
          <svg className="h-5 w-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <div>
            <p className="font-medium text-gray-700">Email:</p>
            <p className="text-gray-600">{formData.email}</p>
          </div>
        </div>
        
        <div className="flex items-center">
          <Wrench className="h-5 w-5 text-gray-500 mr-3" />
          <div>
            <p className="font-medium text-gray-700">Serviciu:</p>
            <p className="text-gray-600">
              {services.find(s => s.id === formData.serviceId)?.name || 'Serviciu selectat'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center">
          <Calendar className="h-5 w-5 text-gray-500 mr-3" />
          <div>
            <p className="font-medium text-gray-700">Data și ora:</p>
            <p className="text-gray-600">{formData.day} {formData.month}, {formData.timeSlot}</p>
          </div>
        </div>
      </div>
      
      <div className="flex space-x-4">
        <button
          onClick={handleBack}
          disabled={loading}
          className="flex-1 text-white py-3 px-4 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{backgroundColor: loading ? '#6b7280' : '#3d3c41'}}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = '#2d2c31';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = '#3d3c41';
            }
          }}
        >
          <ArrowLeft className="h-5 w-5 inline mr-2" />
          Înapoi
        </button>
        <button
          onClick={handleConfirm}
          disabled={loading}
          className="flex-1 bg-green-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline mr-2"></div>
              Se confirmă...
            </>
          ) : (
            <>
              <CheckCircle className="h-5 w-5 inline mr-2" />
              Confirmă Programarea
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-4" style={{background: 'linear-gradient(135deg, #fef2f2 0%, #fef2f2 100%)'}}>
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-xl shadow-xl p-6">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-500">Pasul {currentStep} din 6</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${(currentStep / 6) * 100}%`,
                  backgroundColor: '#971b1e'
                }}
              ></div>
            </div>
          </div>
          
          {/* Step content */}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep4()}
          {currentStep === 4 && renderStep5()}
          {currentStep === 5 && renderStep6()}
          {currentStep === 6 && renderStep7()}
        </div>
        
        <div className="text-center mt-6 text-gray-500 text-sm">
          <p>© 2025 Imotrust Facility Management</p>
        </div>
      </div>
    </div>
  );
};

export default HeatingAppointmentApp;
